/**
 * `useArrowStep(onStep)` — ← and → step through peers, as the demo's global
 * key handler did (line 2171): on the machine page they step through the
 * machine's subclass, on a division page they step the first hero.
 *
 * Ignored while the reader is typing — in an input, a textarea, a select or
 * anything contenteditable — and when a modifier is held, so the browser's own
 * caret, history (⌘←) and word-jump (⌥←) bindings keep working.
 *
 * UI state for the fleet scenes; lives beside them.
 */

import { useEffect, useRef } from 'react';

const isTyping = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || target.matches('input, select, textarea'));

/**
 * @param onStep - called with `-1` for ← and `1` for →. Pass `null` to
 *   disable (the listener is still one, stable subscription).
 */
export function useArrowStep(onStep: ((delta: -1 | 1) => void) | null): void {
  const handler = useRef(onStep);
  handler.current = onStep;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.defaultPrevented || isTyping(event.target) || !handler.current) return;
      event.preventDefault();
      handler.current(event.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
